import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Badge, Row, Col, Spinner } from 'reactstrap';
import QRCode from 'react-qr-code';
import moment from 'moment';
import { CheckCircle, Clock, Calendar, Users, Phone, User, MapPin } from 'react-feather';
import { Get_Meal_Pass_Detail } from '../../api/MealPassApi/MealPassApi';

const MealPassDetailModal = ({ isOpen, toggle, mealPass }) => {
  const [detailData, setDetailData] = useState(mealPass);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setDetailData(mealPass);
    if (isOpen && mealPass?.mealId) {
      setLoading(true);
      Get_Meal_Pass_Detail(mealPass.mealId)
        .then(({ data }) => {
          if (data?.status === 200 && data.payload) {
            setDetailData(data.payload);
          }
        })
        .catch((err) => console.error('Error fetching meal pass detail:', err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, mealPass?.mealId]);

  const currentPass = detailData || mealPass;
  if (!currentPass && !isOpen) return null;
  const safePass = currentPass || {};

  const getSlotTypeName = (type) => {
    switch (type) {
      case 'breakFast':
        return 'Breakfast (अल्पाहार)';
      case 'lunch':
        return 'Lunch (दोपहर भोजन)';
      case 'dinner':
        return 'Dinner (रात्रि भोजन)';
      default:
        return type || 'Meal';
    }
  };

  const totalSlots = safePass.mealPassScans?.length || 0;
  const takenSlots = safePass.mealPassScans?.filter((s) => s.isMealTaken)?.length || 0;

  return (
    <Modal
      isOpen={Boolean(isOpen && currentPass)}
      toggle={toggle}
      onClosed={() => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
      }}
      size="lg"
      centered
      className="meal-pass-modal"
    >
      <ModalHeader toggle={toggle} className="border-0 pb-0">
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: '24px' }}>🎟️</span>
          <div>
            <h5 className="mb-0 fw-bold">Bhojan Pass Ticket Details</h5>
            <small className="text-muted">Pass ID: #{safePass.mealId || ''}</small>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="pt-2">
        <Row className="g-3">
          {/* Left Column: QR Code & Devotee Card */}
          <Col md="5">
            <div className="ticket-preview-box">
              <div className="ticket-header-title">Bhojan Pass</div>
              <div className="ticket-sub-title">
                {currentPass.trustDetail?.trustName || 'Swaminarayan Temple / Bhojanshala'}
              </div>

              <div className="qr-code-wrapper">
                {currentPass.qrCode ? (
                  <QRCode value={currentPass.qrCode} size={150} level="M" />
                ) : (
                  <div style={{ width: 150, height: 150, background: '#f1f5f9' }} />
                )}
              </div>

              <div className="mb-2">
                <Badge color={takenSlots === totalSlots && totalSlots > 0 ? 'secondary' : 'success'} className="px-3 py-2">
                  {takenSlots} of {totalSlots} Meals Consumed
                </Badge>
              </div>

              <div className="text-muted small mt-2">
                <code style={{ fontSize: '11px', color: '#475569' }}>{currentPass.qrCode}</code>
              </div>
            </div>

            <div className="card p-3 mt-3 border bg-light rounded-3">
              {currentPass.trustDetail?.trustName && (
                <div className="d-flex align-items-center gap-2 mb-2 text-primary small fw-semibold">
                  <span>🏛️</span>
                  <span>{currentPass.trustDetail.trustName}</span>
                </div>
              )}
              <div className="d-flex align-items-center gap-2 mb-2">
                <User size={16} className="text-primary" />
                <span className="fw-bold">{currentPass.personDetail?.name || 'Devotee / Guest'}</span>
              </div>
              {currentPass.personDetail?.mobile && (
                <div className="d-flex align-items-center gap-2 mb-2 text-muted small">
                  <Phone size={14} />
                  <span>{currentPass.personDetail.mobile}</span>
                </div>
              )}
              {(currentPass.personDetail?.village || currentPass.personDetail?.city) && (
                <div className="d-flex align-items-center gap-2 mb-2 text-muted small">
                  <MapPin size={14} />
                  <span>
                    {[currentPass.personDetail?.village, currentPass.personDetail?.city, currentPass.personDetail?.state]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </div>
              )}
              <div className="d-flex align-items-center gap-2 mb-2 text-muted small">
                <Users size={14} />
                <span>{currentPass.person} Person(s) per pass</span>
              </div>
              <div className="d-flex align-items-center gap-2 text-muted small">
                <Calendar size={14} />
                <span>
                  {moment(currentPass.startDate).format('DD MMM YYYY')} – {moment(currentPass.endDate).format('DD MMM YYYY')}
                </span>
              </div>
            </div>
          </Col>

          {/* Right Column: Scheduled Slots Table */}
          <Col md="7">
            <h6 className="fw-bold mb-3 d-flex align-items-center justify-content-between">
              <span>Scheduled Meal Slots ({totalSlots})</span>
              <span className="text-muted small fw-normal">
                {takenSlots}/{totalSlots} Completed
              </span>
            </h6>

            <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-5 text-muted">
                  <Spinner size="sm" color="primary" className="me-2" /> Loading pass details...
                </div>
              ) : (
                <Table responsive size="sm" className="align-middle border">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Slot</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(currentPass.mealPassScans || []).map((scan) => (
                      <tr key={scan.id}>
                        <td style={{ fontSize: '13px' }}>
                          <strong>{moment(scan.takenDate).format('DD MMM YYYY')}</strong>
                          <div className="text-muted small">{moment(scan.takenDate).format('dddd')}</div>
                        </td>
                        <td>
                          <div className="fw-semibold" style={{ fontSize: '13px' }}>
                            {getSlotTypeName(scan.mealTimeSlot?.type)}
                          </div>
                          {scan.mealTimeSlot?.startTime && (
                            <div className="text-muted small" style={{ fontSize: '11px' }}>
                              {moment(scan.mealTimeSlot.startTime, 'HH:mm:ss').format('hh:mm A')} –{' '}
                              {moment(scan.mealTimeSlot.endTime, 'HH:mm:ss').format('hh:mm A')}
                            </div>
                          )}
                        </td>
                        <td>
                          {scan.isMealTaken ? (
                            <Badge color="success" className="d-inline-flex align-items-center gap-1">
                              <CheckCircle size={11} /> Served ({scan.takenPerson || currentPass.person}p)
                            </Badge>
                          ) : (
                            <Badge color="warning" className="d-inline-flex align-items-center gap-1">
                              <Clock size={11} /> Pending
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                    {(!currentPass.mealPassScans || currentPass.mealPassScans.length === 0) && (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-3">
                          No scan slots recorded.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter className="border-0 pt-0">
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MealPassDetailModal;
